import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Col, Form, Row } from 'react-bootstrap';
import { ChevronRight, Search } from 'react-bootstrap-icons';
import { ENDPOINTS } from '../../helpers/constants';
import { boardRequest } from './helpers';
import LoadMore from './LoadMore';
import {
  CardSkeletonGrid,
  LoadingStatus,
  MoneyCardSkeleton,
  SectionHeaderSkeleton,
  StatButtonSkeleton
} from './Skeletons';

const money = value =>
  Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function DealRow({ deal, onOpen }) {
  const attention = deal.bucket === 'needs_attention';
  return (
    <Col md={6} xl={4} className="mb-3">
      <div
        className="today-row bg-white border rounded p-3 h-100"
        role="button"
        onClick={() => onOpen(deal)}
      >
        <div className="d-flex align-items-start">
          <div className="flex-fill min-width-0">
            <div className="d-flex flex-wrap align-items-center">
              <span className="font-weight-bold text-dark text-truncate">{deal.buyer}</span>
              <Badge variant="light" className="ml-2 border">
                {deal.status}
              </Badge>
            </div>
            <div className="small text-muted mt-1">Sales price {money(deal.salesPrice)}</div>
            <div className="mt-2">Commission due {money(deal.commissionDue)}</div>
            {deal.bucket === 'funded' && (
              <div className="small text-muted mt-1">
                <div>Already advanced {money(deal.advanced)}</div>
                <div>Remaining balance {money(deal.remaining)}</div>
                {deal.fundedDate ? <div>Funded {deal.fundedDate}</div> : null}
              </div>
            )}
            {deal.bucket !== 'funded' && (
              <div className={`small mt-1 ${attention ? 'text-danger' : 'text-muted'}`}>{deal.reason}</div>
            )}
          </div>
          <ChevronRight className="text-muted ml-2" />
        </div>
      </div>
    </Col>
  );
}

function MyMoneyTab({ scopeQuery, reloadKey = 0, onOpen }) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState('all');
  const [moneyBook, setMoneyBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestId = useRef(0);
  const groups = moneyBook?.groups || [];
  const showingAll = bucket === 'all';
  const active = groups.find(group => group.key === bucket);
  const rows = showingAll ? groups.flatMap(group => group.rows || []) : active?.rows || [];

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(
    async (page, append) => {
      const id = ++requestId.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      const response = await boardRequest(ENDPOINTS.CONTACTS_MONEY, scopeQuery, {
        page,
        bucket,
        search,
        fresh: !append && reloadKey ? '1' : '0'
      });
      if (id !== requestId.current) return;
      setLoading(false);
      setLoadingMore(false);
      if (!response) return;
      setMoneyBook(current => {
        if (!append || !current) return response;
        return {
          ...response,
          groups: response.groups.map(group => {
            const previous = current.groups.find(item => item.key === group.key);
            const seen = new Set((previous?.rows || []).map(row => row._id));
            return {
              ...group,
              rows: [...(previous?.rows || []), ...(group.rows || []).filter(row => !seen.has(row._id))]
            };
          })
        };
      });
    },
    [scopeQuery, bucket, search, reloadKey]
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  const loadMore = () => {
    if (!moneyBook?.hasMore || loading || loadingMore) return;
    load((moneyBook.page || 1) + 1, true);
  };

  return (
    <>
      <Row className="align-items-end mb-4">
        <Col md={7} className="mb-3 mb-md-0">
          <div className="text-primary small font-weight-bold mb-1">COMMISSION</div>
          <h2 className="font-weight-bold mb-1">My Money</h2>
          <p className="text-muted mb-0">
            Same commission figure the contract screen already shows. A deal appears when it has a sales price and an inventory unit.
          </p>
        </Col>
        <Col md={5}>
          <div className="d-flex align-items-center border bg-white rounded px-3">
            <Search className="text-muted mr-2" />
            <Form.Control
              className="border-0 px-0 shadow-none"
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              placeholder="Search deals"
            />
          </div>
        </Col>
      </Row>

      {loading ? (
        <>
          <LoadingStatus label="Loading commission" />
          <Row className="mb-4">
            {Array.from({ length: 5 }, (_, index) => (
              <StatButtonSkeleton key={index} />
            ))}
          </Row>
          <section className="mb-4">
            <SectionHeaderSkeleton />
            <CardSkeletonGrid count={3} Card={MoneyCardSkeleton} />
          </section>
          <section className="mb-4">
            <SectionHeaderSkeleton />
            <CardSkeletonGrid count={3} Card={MoneyCardSkeleton} />
          </section>
        </>
      ) : (
      <Row className="mb-4">
        <Col xs={6} md className="mb-2">
          <button
            type="button"
            className={`btn text-left w-100 px-3 py-2 ${showingAll ? 'btn-primary' : 'btn-light border bg-white'}`}
            onClick={() => setBucket('all')}
          >
            <div className="h5 font-weight-bold mb-0">{money(moneyBook?.commissionDue)}</div>
            <div className={`small ${showingAll ? '' : 'text-muted'}`}>All</div>
          </button>
        </Col>
        {groups.map(group => {
          const selected = !showingAll && group.key === bucket;
          return (
            <Col key={group.key} xs={6} md className="mb-2">
              <button
                type="button"
                className={`btn text-left w-100 px-3 py-2 ${selected ? 'btn-primary' : 'btn-light border bg-white'}`}
                onClick={() => setBucket(group.key)}
              >
                <div className="h5 font-weight-bold mb-0">{money(group.commissionDue)}</div>
                <div className={`small ${selected ? '' : 'text-muted'}`}>
                  {group.label} · {group.count}
                </div>
              </button>
            </Col>
          );
        })}
      </Row>
      )}

      {!loading &&
        (showingAll ? (
          groups
            .filter(group => group.rows.length > 0)
            .map(group => (
              <section key={group.key} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="h5 font-weight-bold mb-0">{group.label}</h3>
                  <Badge variant="light" className="border">
                    {money(group.commissionDue)}
                  </Badge>
                </div>
                <Row>
                  {group.rows.map(deal => (
                    <DealRow key={deal._id} deal={deal} onOpen={onOpen} />
                  ))}
                </Row>
              </section>
            ))
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="h5 font-weight-bold mb-0">{active?.label || 'My Money'}</h3>
              <Badge variant="light" className="border">
                {active?.count || 0} deals
              </Badge>
            </div>
            <Row>
              {rows.map(deal => (
                <DealRow key={deal._id} deal={deal} onOpen={onOpen} />
              ))}
            </Row>
          </>
        ))}

      {!loading && <LoadMore hasMore={moneyBook?.hasMore} loading={loadingMore} onLoad={loadMore} />}

      {!loading && rows.length === 0 && (
        <div className="bg-white rounded p-4 text-center text-muted" style={{ border: '1px dashed #dee2e6' }}>
          No deals with a sales price and an inventory unit
        </div>
      )}
    </>
  );
}

export default MyMoneyTab;
